import { Button, Tooltip } from "@/ds";
import { Icon } from "@/lib";
import { useT } from "../i18n";
import { ShellRouteLink } from "../shell-sidebar";
import { MailboxRefresh } from "./mailbox-refresh";
import { OverviewIncomingWork, type IncomingWorkRead } from "./overview-incoming-work";
import type { InboxTaskFilter } from "./screen-inbox.logic";

// #2107: one glyph size for the whole row of head tools, above the usual 18 px (see .ov-head__tools).
const HEAD_ICON_SIZE = 22;
// #2002: the two lists that hold no open work are reached from the page head.
// #2103: so is the list of all open work, since its own row above the list is gone.
const HEAD_LISTS = [
  { list: "all", icon: "list", label: "overview.seeAll" },
  { list: "archived", icon: "archive", label: "inbox.task.archived" },
  { list: "trash", icon: "trash", label: "inbox.task.trash" },
] as const;

export type OverviewHeadList = (typeof HEAD_LISTS)[number]["list"];

/**
 * The Overview's head tools: processing status, mailbox refresh, the page's filter toggle and the
 * switches between lists. #2259: the Filed page shows the same row, so every list keeps them.
 */
export function OverviewHeadTools(props: {
  incoming: IncomingWorkRead;
  filter: { expanded: boolean; controls: string; testId: string; onToggle: () => void };
  /** The list the page shows; Filed is a page of its own. */
  current: InboxTaskFilter | "filed";
  onSelectList: (list: OverviewHeadList) => void;
  /** #2104: the way to Filed. App owns the route; the count is its team-unread mail. */
  filed?: { href: string; count: number; onOpen: () => void };
}) {
  const t = useT();
  const { filed } = props;
  const listSwitch = (entry: (typeof HEAD_LISTS)[number]) => (
    <Tooltip key={entry.list} content={t(entry.label)} placement="bottom">
      <Button variant="ghost" size="sm" iconOnly aria-label={t(entry.label)}
        aria-pressed={props.current === entry.list}
        data-testid={`overview-open-${entry.list}`} onClick={() => props.onSelectList(entry.list)}>
        <Icon name={entry.icon} size={HEAD_ICON_SIZE} />
      </Button>
    </Tooltip>
  );
  const filedLabel = filed && filed.count > 0 ? t("overview.filedOpen", { n: filed.count }) : t("shell.filed");
  const filedCount = filed && filed.count > 0
    ? <span className="ov-head__count" aria-hidden="true">{filed.count > 99 ? "99+" : filed.count}</span>
    : null;

  return (
    <div className="ov-head__tools">
      <OverviewIncomingWork read={props.incoming} />
      <MailboxRefresh />
      <Tooltip content={t("inbox.filterAria")} placement="bottom">
        <Button variant="ghost" size="sm" iconOnly aria-label={t("inbox.filterAria")}
          aria-expanded={props.filter.expanded} aria-controls={props.filter.controls}
          data-testid={props.filter.testId} onClick={props.filter.onToggle}>
          <Icon name="filter" size={HEAD_ICON_SIZE} />
        </Button>
      </Tooltip>
      <div className="ov-head__views" role="group" aria-label={t("overview.views.aria")}>
        {listSwitch(HEAD_LISTS[0])}
        {/* #2104: Filed is a page of its own, so from a list it is a link: a new tab, Back and a
            deep link work as for every destination. The count stops people having to look. On
            Filed itself it is the pressed switch, like the current list on the Overview. */}
        {filed && (
          <Tooltip content={filedLabel} placement="bottom">
            {props.current === "filed" ? (
              <Button variant="ghost" size="sm" iconOnly aria-label={filedLabel} aria-pressed
                data-testid="overview-open-filed">
                <Icon name="mailX" size={HEAD_ICON_SIZE} />
                {filedCount}
              </Button>
            ) : (
              <ShellRouteLink href={filed.href} className="neo-btn neo-btn--ghost neo-btn--sm neo-btn--icon"
                aria-label={filedLabel} data-testid="overview-open-filed" onNavigate={filed.onOpen}>
                <Icon name="mailX" size={HEAD_ICON_SIZE} />
                {filedCount}
              </ShellRouteLink>
            )}
          </Tooltip>
        )}
        {HEAD_LISTS.slice(1).map(listSwitch)}
      </div>
    </div>
  );
}
